import React, { useState, useEffect } from 'react';
import {
  Box, Title, Text, Stepper, Group, Button, Alert, Radio, Stack
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '../../shared/contexts/AuthContext';
import PlatformBackButton from '../../components/PlatformBackButton';
import DiagramUploadStep from './components/DiagramUploadStep';
import DiagramPageReviewStep from './components/DiagramPageReviewStep';
import TenantContextStep from './components/TenantContextStep';
import GenerationProgressStep from './components/GenerationProgressStep';
import {
  submitFullBuildJob, pollAgentJob, listTenantProfiles
} from './api_scripts/agentStudioClient';

/**
 * "From Diagram" wizard — multi-step UI for the Full Build feature.
 *
 *   Step 1  Upload PDF
 *   Step 2  Confirm pages → bots (edit names, types, skip pages)
 *   Step 3  Tenant context (pick profile or create new)
 *   Step 4  Generate + download
 */
function DiagramWizard({ onBackToPlatformSelector }) {
  const { getApiToken } = useAuth();
  const [activeStep, setActiveStep] = useState(0);

  // Step 1 — upload
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfBase64, setPdfBase64] = useState('');

  // Step 2 — page roster (will be populated by classifier in a real run;
  // for v1 the user enters the roster manually based on what they see in
  // the PDF preview — the classifier integration is a v2 enhancement)
  const [pageRoster, setPageRoster] = useState([]);

  // Step 3 — tenant context
  const [tenantProfiles, setTenantProfiles] = useState([]);
  const [tenantProfilesLoading, setTenantProfilesLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState('');

  // Step 4 — generation
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [error, setError] = useState(null);

  // Step 3b — flowType selector. SUBFLOW is the Presidio-recommended
  // default: reusable, portable across tenants, and avoids the Main-flow
  // channels config that the synthesizer can't populate reliably.
  const [flowType, setFlowType] = useState('SUBFLOW');

  // Load tenant profiles on mount
  useEffect(() => {
    let cancelled = false;
    setTenantProfilesLoading(true);
    listTenantProfiles(getApiToken)
      .then((res) => {
        if (cancelled) return;
        setTenantProfiles(res?.profiles || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('Failed to load tenant profiles:', err);
      })
      .finally(() => {
        if (!cancelled) setTenantProfilesLoading(false);
      });
    return () => { cancelled = true; };
  }, [getApiToken]);

  // Job polling
  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const row = await pollAgentJob(jobId, getApiToken);
        if (cancelled) return;
        setJobStatus(row);
        if (row.status === 'SUCCEEDED' || row.status === 'FAILED') return;
        setTimeout(poll, 3000);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [jobId, getApiToken]);

  const handleGenerate = async () => {
    setError(null);
    try {
      const res = await submitFullBuildJob({
        pdfBase64,
        tenantOrgId: selectedOrgId,
        pageRoster,
        flowType
      }, getApiToken);
      setJobId(res.jobId);
      setActiveStep(3);
    } catch (err) {
      setError(err.message);
    }
  };

  const canAdvance = (step) => {
    if (step === 0) return !!pdfBase64;
    if (step === 1) return pageRoster.some((p) => p.role === 'bot' && !p.skip);
    if (step === 2) return !!selectedOrgId;
    return false;
  };

  return (
    <Box style={{ padding: '24px 32px' }}>
      <Group mb="lg" justify="space-between">
        <Box>
          <Title order={2}>From Diagram</Title>
          <Text c="dimmed" size="sm">
            Upload a multi-page Lucid PDF. Get back one AI Agent JSON per bot page + one importable WxCC Flow Designer JSON.
          </Text>
        </Box>
        <PlatformBackButton onClick={onBackToPlatformSelector} color="cyan" mb={0} />
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md" onClose={() => setError(null)} withCloseButton>
          {error}
        </Alert>
      )}

      <Stepper active={activeStep} onStepClick={setActiveStep} color="cyan" allowNextStepsSelect={false}>
        <Stepper.Step label="Upload" description="Lucid PDF">
          <Box mt="lg">
            <DiagramUploadStep
              pdfFile={pdfFile}
              onPdfChange={(file, base64) => {
                setPdfFile(file);
                setPdfBase64(base64);
                // Seed an empty roster — Step 2 lets the user add bot rows
                setPageRoster([]);
              }}
            />
          </Box>
        </Stepper.Step>

        <Stepper.Step label="Confirm pages" description="Bot roster">
          <Box mt="lg">
            <DiagramPageReviewStep
              roster={pageRoster}
              onChange={setPageRoster}
            />
          </Box>
        </Stepper.Step>

        <Stepper.Step label="Tenant" description="Schema profile">
          <Box mt="lg">
            <TenantContextStep
              profiles={tenantProfiles}
              loading={tenantProfilesLoading}
              selectedOrgId={selectedOrgId}
              onSelect={setSelectedOrgId}
              onProfileSaved={(profile) => {
                setTenantProfiles((existing) => {
                  const filtered = existing.filter((p) => p.orgId !== profile.orgId);
                  return [...filtered, profile];
                });
                setSelectedOrgId(profile.orgId);
              }}
              getApiToken={getApiToken}
            />

            <Box mt="xl" p="md" style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }}>
              <Text fw={600} mb={4}>Output flow type</Text>
              <Text size="xs" c="dimmed" mb="sm">
                Choose whether the generated flow is a reusable Subflow (recommended)
                or a top-level Main Flow. Subflows are more portable and avoid the
                per-tenant channels configuration that Main flows require.
              </Text>
              <Radio.Group value={flowType} onChange={setFlowType}>
                <Stack gap="xs">
                  <Radio
                    value="SUBFLOW"
                    label="Subflow (recommended)"
                    description="Reusable, called from your existing Main flow via CallFlow. Terminals use EndSubflow."
                  />
                  <Radio
                    value="FLOW"
                    label="Main Flow"
                    description="Top-level flow with Start / DisconnectContact / QueueContact terminals. Requires channels config in your tenant."
                  />
                </Stack>
              </Radio.Group>
            </Box>
          </Box>
        </Stepper.Step>

        <Stepper.Step label="Generate" description="Wait + download">
          <Box mt="lg">
            <GenerationProgressStep
              jobId={jobId}
              jobStatus={jobStatus}
              onRetry={handleGenerate}
              getApiToken={getApiToken}
              onRetryFromImportError={(resp) => {
                // D2: fail-path — the retry endpoint returned a new jobId.
                // Swap it in and let the existing pollAgentJob effect pick up
                // the new PENDING → RUNNING → SUCCEEDED cycle.
                setJobId(resp.jobId);
                setJobStatus({ status: resp.status || 'PENDING' });
              }}
            />
          </Box>
        </Stepper.Step>
      </Stepper>

      <Group mt="xl" justify="space-between">
        <Button variant="default" disabled={activeStep === 0} onClick={() => setActiveStep(activeStep - 1)}>
          Back
        </Button>
        {activeStep < 2 && (
          <Button color="cyan" disabled={!canAdvance(activeStep)} onClick={() => setActiveStep(activeStep + 1)}>
            Next
          </Button>
        )}
        {activeStep === 2 && (
          <Button color="cyan" disabled={!canAdvance(2)} onClick={handleGenerate}>
            Generate
          </Button>
        )}
      </Group>
    </Box>
  );
}

export default DiagramWizard;
